<!-- DTR Copilot Panel — bottom-left overlay -->

<div class="copilot-host" [class.expanded]="isExpanded" [class.maximized]="isMaximized">

  <!-- ── Collapsed trigger button ── -->
  <button
    *ngIf="copilotOrgEnabled"
    mat-fab
    class="copilot-trigger"
    [color]="modeColor"
    (click)="toggle()"
    matTooltip="DTR Copilot"
    [matBadge]="unreadCount > 0 ? unreadCount : null"
    matBadgeColor="warn"
    matBadgeSize="small">
    <mat-icon>auto_awesome</mat-icon>
  </button>

  <!-- ── Expanded panel ── -->
  <mat-card class="copilot-card" *ngIf="isExpanded" [@slideUp] cdkDrag cdkDragBoundary="body" [style.width]="panelWidthStyle" [style.height]="cardHeightStyle">

    <!-- Header -->
    <mat-card-header class="copilot-header" cdkDragHandle>
      <div class="header-grid">

        <!-- Col 1: identity icon -->
        <mat-icon class="copilot-icon">smart_toy</mat-icon>

        <!-- Col 2, row 1: title + action buttons -->
        <div class="header-title-row">
          <div class="header-text">
            <span class="copilot-title">DTR Copilot</span>
            <span class="copilot-subtitle">{{ transformLabel }}</span>
          </div>
          <span class="flex-spacer"></span>
          <button mat-icon-button matTooltip="New Session" (click)="resetSession()" [disabled]="isProcessing">
            <mat-icon>add_comment</mat-icon>
          </button>
          <button mat-icon-button
                  [matTooltip]="surveyEnabled ? 'Feedback survey on — click to disable' : 'Feedback survey off — click to enable'"
                                   (click)="toggleSurvey()">
            <mat-icon>{{ surveyEnabled ? 'rate_review' : 'speaker_notes_off' }}</mat-icon>
          </button>
          <button mat-icon-button [matTooltip]="isMaximized ? 'Restore default size' : 'Maximise size'" (click)="toggleSize()">
            <mat-icon>{{ isMaximized ? 'fullscreen_exit' : 'fullscreen' }}</mat-icon>
          </button>
          <button mat-icon-button matTooltip="Close" (click)="toggle()">
            <mat-icon>close</mat-icon>
          </button>
        </div>

        <!-- Col 2, row 2: mode controls -->
        <div class="header-row--controls">
          <span class="control-label"
                matTooltip="Builder: Copilot builds and applies rules. Advisor: recommendations only. Auto: fully autonomous.">
            Mode
          </span>
          <div class="header-chips">
            <button class="header-chip" *ngFor="let m of ['builder','advisor','autonomous']"
                    [class.header-chip--active]="mode === m"
                    (click)="setMode($any(m))">
              {{ m === 'autonomous' ? '⚡ Auto' : (m | titlecase) }}
            </button>
          </div>
        </div>

        <!-- Col 2, row 3: depth controls -->
        <div class="header-row--controls">
          <span class="control-label"
                matTooltip="Controls how thoroughly Copilot builds and cleans rules. Standard: single optimisation pass — fastest. Deep Dive: multiple passes plus AI contamination fix — cleaner rules, slower. Deepest: iterative AI remediation until contamination is eliminated — most thorough, slowest. Build time scales with farm size and rule count. Increase Depth when rule quality matters more than build speed. Tip: Depth controls HOW thoroughly each tier is built — use Min Tier to control WHICH tiers are worth building at all.">
            Depth
          </span>
          <div class="header-chips">
            <button class="header-chip"
                    [class.header-chip--active]="copilotSettings.depth === 0"
                    (click)="copilotSettings.setDepth(0)">
              Standard
            </button>
            <button class="header-chip"
                    [class.header-chip--active]="copilotSettings.depth === 1"
                    (click)="copilotSettings.setDepth(1)">
              ⚡ Deep Dive
            </button>
            <button class="header-chip"
                    [class.header-chip--active]="copilotSettings.depth === 2"
                    (click)="copilotSettings.setDepth(2)">
              <span class="icon-deepest">⚡</span> Deepest
            </button>
          </div>
        </div>

      </div>
    </mat-card-header>

    <!-- Daily credit usage bar — full width of the panel, sitting right below the
         blue header, outside its padding. Visible any time usageStatus exists,
         i.e. any time a daily limit is configured (see _yield_usage_status_event) —
         regardless of level. level only ever means "is usage high enough for
         Copilot to proactively nag about it" (build_system_prompt_fact's 'none'
         threshold, 50%) — a DIFFERENT concern from "should the Analyst see their
         current usage," which should always be visible. RCA 2026-09-19: gating
         visibility on level conflated the two, hiding the bar for the entire
         0-49% range even though the bar itself renders correctly off raw
         usagePercent at any value. -->
    <div class="usage-bar-wrap" *ngIf="usageStatus">
      <app-usage-progress-bar
          [usagePercent]="usageStatus.usagePercent"
          [squareEnds]="true"
          [thin]="true"
          [darkText]="true"
          [clickable]="false"
          [tooltipText]="usageStatus.usedCredits.toFixed(0) + ' of ' + usageStatus.limitCredits.toFixed(0) + ' daily credits used'">
      </app-usage-progress-bar>
    </div>

    <!-- Chat window -->
    <mat-card-content class="chat-window" #chatWindow>

      <ng-container *ngFor="let msg of messages; trackBy: trackByMsgId">

        <!-- Unified advisor offer (sort, sample range) -->
        <div *ngIf="msg.role === 'system' && msg.subType === 'advisor_offer'" class="msg-sort-offer">
          <div class="sort-offer-text">
            <mat-icon class="sort-offer-icon">{{ advisorAdvice?.type === 'sample_range' ? 'tune' : 'sort' }}</mat-icon>
            <span>{{ msg.content }}</span>
          </div>
          <div class="sort-offer-actions">
            <button mat-stroked-button color="primary" (click)="onAdvisorAccept()">Apply</button>
            <button mat-stroked-button (click)="onAdvisorSkip()">Skip</button>
          </div>
        </div>

        <!-- System message -->
        <div *ngIf="msg.role === 'system' && msg.subType !== 'advisor_offer'" class="msg-system"
             [class.msg-system--warning]="msg.subType === 'depth_warning'">
          <mat-icon class="msg-icon-system" [class.msg-icon-warning]="msg.subType === 'depth_warning'">
            {{ msg.subType === 'depth_warning' ? 'warning_amber' : 'info_outline' }}
          </mat-icon>
          <span>{{ msg.content }}</span>
        </div>

        <!-- User message -->
        <div *ngIf="msg.role === 'user'" class="msg-row msg-user">
          <div class="msg-bubble msg-bubble-user">{{ msg.content }}</div>
        </div>

        <!-- Assistant message -->
        <div *ngIf="msg.role === 'assistant'" class="msg-row msg-assistant">
          <div class="msg-body">

            <!-- Tool activity indicator -->
            <div *ngIf="msg.toolActivity && msg.toolActivity.length > 0" class="tool-activity">
              <span *ngFor="let t of msg.toolActivity" class="tool-chip"
                    [class.tool-running]="t.phase === 'running'"
                    [class.tool-done]="t.phase === 'done'">
                <mat-icon class="tool-icon">{{ t.phase === 'running' ? 'sync' : 'check_circle' }}</mat-icon>
                {{ t.name | copilotToolLabel : t.input }}
              </span>
            </div>

            <!-- Message text (build commentary / stats) -->
            <div class="msg-bubble msg-bubble-assistant"
                 [class.streaming]="msg.isStreaming"
                 [innerHTML]="msg.content | copilotMarkdown">
            </div>

            <!-- All labels — issues directly above decision/option, in one connected block -->
            <div *ngIf="(issueLabels(msg).length + actionLabels(msg).length) > 0"
                 class="cp-labels"
                 [class.cp-labels--action]="actionLabels(msg).length > 0">
              <div *ngFor="let label of issueLabels(msg)" class="cp-label cp-label--issue">
                <span class="cp-label-heading">Issue<ng-container *ngIf="issueLabels(msg).length > 1"> {{ label.n }}</ng-container><ng-container *ngIf="label.title"> — {{ label.title }}</ng-container>:</span>
                <span class="cp-label-body">{{ label.body }}</span>
              </div>
              <div *ngFor="let label of actionLabels(msg)" class="cp-label" [ngClass]="'cp-label--' + label.type">
                <span class="cp-label-heading">
                  <ng-container *ngIf="label.type === 'decision'">Decision:</ng-container>
                  <ng-container *ngIf="label.type === 'option'">Option {{ label.letter }}:</ng-container>
                </span>
                <span class="cp-label-body">{{ label.body }}</span>
              </div>
            </div>

            <!-- Typing cursor when streaming -->
            <span *ngIf="msg.isStreaming" class="typing-cursor">▋</span>

            <!-- Insight card -->
            <div *ngIf="msg.insight" class="insight-card" [class]="'insight-' + msg.insight.severity">
              <mat-icon class="insight-icon">
                {{ msg.insight.type === 'misuse' ? 'swap_horiz' :
                   msg.insight.type === 'preparation' ? 'build' : 'tips_and_updates' }}
              </mat-icon>
              <div class="insight-body">
                <div class="insight-message">{{ msg.insight.message }}</div>
                <div *ngIf="msg.insight.suggestedTransformName" class="insight-action">
                  Suggested: <strong>{{ msg.insight.suggestedTransformName }}</strong>
                </div>
              </div>
            </div>

            <!-- Row 1: Tier choice buttons (Tier 2 | Tier 3 | Skip) / Accept | Skip / Option A | Option B -->
            <div *ngIf="msg.choices && msg.choices.length > 0" class="choice-buttons">
              <button *ngFor="let choice of msg.choices; let i = index"
                      mat-stroked-button
                      color="primary"
                      [class.choice-btn--recommended]="choice.value === msg.recommendedChoice"
                      [matTooltip]="choice.value === msg.recommendedChoice
                        ? 'Recommended: ' + choice.label
                        : choice.label"
                      matTooltipShowDelay="600"
                      [disabled]="msg.choiceSelected || isProcessing"
                      (click)="onTierChoice(msg, choice)">
                {{ isOptionChoice(choice.value) ? 'Option ' + optionLetters[i] : choice.label }}
              </button>
            </div>

            <!-- Row 2: Action buttons — shown only on the latest message after tier build or analyze -->
            <div *ngIf="msg.id === lastAssistantMessageId && (hasTierChoices(msg.choices) || msgHasAnalysisActivity(msg)) && !msg.isStreaming && hasAuditableSession"
                 class="action-buttons">
              <button mat-stroked-button
                      class="dashboard-btn"
                      matTooltip="Show contamination stats and quality scores for the current config."
                      [disabled]="isProcessing"
                      (click)="onDashboardClick()">
                <mat-icon>smart_toy</mat-icon> Dashboard
              </button>
              <button *ngIf="lastNavigationHint"
                      mat-stroked-button
                      class="summary-btn"
                      matTooltip="Navigate to the Results Summary view."
                      [disabled]="isProcessing"
                      (click)="onSummaryClick()">
                <mat-icon>smart_toy</mat-icon> Summary
              </button>
              <button *ngIf="!isProcessing"
                      mat-stroked-button
                      class="run-audit-btn"
                      matTooltip="Run a pass-by-pass audit showing which rule matched each row and what was extracted."
                      (click)="onRunAuditClick()">
                <mat-icon>fact_check</mat-icon> Audit
              </button>
            </div>

            <!-- Advisor: Run Full Analysis button -->
            <div *ngIf="mode === 'advisor'
                        && msg.id === lastAssistantMessageId
                        && !msg.isStreaming
                        && !isProcessing
                        && !msg.proposal
                        && !msgHasAnalysisActivity(msg)"
                 class="run-analysis-row">
              <button mat-raised-button class="run-analysis-btn"
                      matTooltip="Scan the full farm independently and compare against your current config — identifies patterns you may be missing and offers to merge them in."
                      (click)="onRunFullAnalysis()">
                <mat-icon>manage_search</mat-icon> Run Full Analysis
              </button>
            </div>

            <!-- Builder: Analyze Config button — score existing rules without re-building -->
            <ng-container *ngIf="mode === 'builder'
                        && msg.id === lastAssistantMessageId
                        && !msg.isStreaming
                        && !isProcessing
                        && canAnalyzeConfig
                        && !hasTierChoices(msg.choices)
                        && !msgHasAnalysisActivity(msg)">
              <ng-container *ngTemplateOutlet="analyzeConfigBtn"></ng-container>
            </ng-container>

            <!-- Builder: Analyze Input Farm button — Discovery Engine, available regardless
                 of whether a config already has rules (unlike Analyze Config above). -->
            <ng-container *ngIf="mode === 'builder'
                        && msg.id === lastAssistantMessageId
                        && !msg.isStreaming
                        && !isProcessing
                        && !hasTierChoices(msg.choices)
                        && !msgHasAnalysisActivity(msg)">
              <ng-container *ngTemplateOutlet="analyzeInputFarmBtn"></ng-container>
            </ng-container>

            <!-- Proposal card -->
            <div *ngIf="msg.proposal" class="proposal-card">
              <div class="proposal-header">
                <mat-icon color="primary">rule</mat-icon>
                <span>{{ msg.proposal.ruleCount }} rule(s) ready — apply below, then review in DTR's Results Dashboard or Data Preview</span>
              </div>

              <div class="proposal-actions">
                <button mat-raised-button color="primary"
                        [disabled]="isApplyingProposal"
                        (click)="onApproveProposal('replace')">
                  <mat-icon>swap_horiz</mat-icon> Replace Rules
                </button>
                <button *ngIf="msg.proposal.source !== 'run_full_analysis'"
                        mat-stroked-button color="primary"
                        [disabled]="isApplyingProposal"
                        (click)="onApproveProposal('merge')">
                  <mat-icon>merge</mat-icon> Merge Rules
                </button>
                <button mat-stroked-button color="warn"
                        [disabled]="isApplyingProposal"
                        (click)="onRejectProposal()">
                  Revise
                </button>
              </div>
            </div>

          </div>
        </div>

      </ng-container>

      <!-- Starting state -->
      <div *ngIf="messages.length === 0 && !sessionId" class="empty-state">
        <mat-icon class="empty-icon">auto_awesome</mat-icon>
        <p>Starting...</p>
      </div>

      <!-- Ready state -->
      <div *ngIf="messages.length === 0 && sessionId" class="empty-state">
        <mat-icon class="empty-icon">auto_awesome</mat-icon>
        <p>Ready.</p>
        <ng-container *ngIf="mode === 'builder' && canAnalyzeConfig && !isProcessing">
          <ng-container *ngTemplateOutlet="analyzeConfigBtn"></ng-container>
        </ng-container>
        <ng-container *ngIf="mode === 'builder' && !isProcessing">
          <ng-container *ngTemplateOutlet="analyzeInputFarmBtn"></ng-container>
        </ng-container>
      </div>

    </mat-card-content>

    <!-- Shared button template — single definition used in message list and ready state -->
    <ng-template #analyzeConfigBtn>
      <div class="run-analysis-row">
        <button mat-raised-button class="run-analysis-btn"
                matTooltip="Ask Copilot to analyse this config — goals, methodology, quality scoring, and the actual target-field output — then optionally summarize it into the Description field."
                (click)="onAnalyzeExistingConfig()">
          <mat-icon>analytics</mat-icon> Analyze Config
        </button>
      </div>
    </ng-template>

    <ng-template #analyzeInputFarmBtn>
      <div class="run-analysis-row">
        <button mat-raised-button class="run-analysis-btn"
                matTooltip="DTR Copilot Discovery Engine — profiles the input farm (independent of any config) to find where the Row Extractor could add value: farm type, and which columns are worth extracting from."
                (click)="onDiscoverInputFarm()">
          <mat-icon>travel_explore</mat-icon> Analyze Input Farm
        </button>
      </div>
    </ng-template>

    <!-- Processing indicator -->
    <div *ngIf="isProcessing || isStopping" class="processing-bar">
      <mat-progress-bar [mode]="isStopping ? 'query' : 'indeterminate'" color="primary"></mat-progress-bar>
      <div class="tool-status-row">
        <span *ngIf="isStopping" class="tool-status">
          <mat-icon class="spin">sync</mat-icon> Stopping...
        </span>
        <span *ngIf="!isStopping && activeToolName" class="tool-status">
          <mat-icon class="spin">sync</mat-icon> {{ activeToolName | copilotToolLabel : activeToolInput }}...
        </span>
        <span *ngIf="!isStopping" class="processing-timer">
          <mat-icon class="timer-icon">timer</mat-icon>{{ processingTime }}
        </span>
      </div>
    </div>

    <!-- DTR Copilot Config Analyser — Summarize into Description. A real button, not an
         LLM-authored checkpoint(choices=...) call — same reason as the goals-source choice
         below: it did not reliably render. -->
    <div *ngIf="showSummarizeAnalysisBtn && !isProcessing" class="run-analysis-row">
      <button mat-raised-button class="run-analysis-btn"
              matTooltip="Write a summary of this analysis into the Description field."
              (click)="summarizeAnalysisIntoDescription()">
        <mat-icon>summarize</mat-icon> Summarize into Description
      </button>
    </div>

    <!-- DTR Copilot Config Analyser — goals-source choice. A real button/text-input pair,
         not an LLM-authored checkpoint(choices=...) call — see onAnalyzeExistingConfig()'s
         comment for why. -->
    <div *ngIf="showAnalysisGoalsPrompt" class="copilot-survey">
      <div class="survey-title">
        <mat-icon class="survey-icon">psychology</mat-icon>
        What are you trying to extract or achieve with this transformer?
      </div>
      <div class="survey-row survey-feedback-row">
        <div class="survey-feedback-field">
          <textarea class="survey-feedback-textarea" maxlength="2000"
            [(ngModel)]="analysisGoalsInput"
            placeholder="Describe your goals for this analysis..."></textarea>
        </div>
      </div>
      <div class="survey-actions">
        <button mat-raised-button class="run-analysis-btn" (click)="inferAnalysisGoals()">
          Infer goals from the config
        </button>
        <button mat-flat-button color="primary" [disabled]="!analysisGoalsInput.trim()"
                (click)="submitAnalysisGoals()">Analyze with these goals</button>
      </div>
    </div>

    <!-- Post-save survey -->
    <div *ngIf="showSurvey" class="copilot-survey">
      <div class="survey-title">
        <mat-icon class="survey-icon">rate_review</mat-icon>
        Transformer saved — how did I do?
      </div>
      <div class="survey-row">
        <span class="survey-label" matTooltip="Overall — how good is the config Copilot built?" matTooltipShowDelay="600">Proposal quality</span>
        <span class="survey-stars">
          <button mat-icon-button *ngFor="let s of surveyStars"
            (click)="setQualityRating(s)"
            [class.survey-star-filled]="(surveyQualityRating ?? 0) >= s">
            <mat-icon>{{ (surveyQualityRating ?? 0) >= s ? 'star' : 'star_border' }}</mat-icon>
          </button>
        </span>
      </div>
      <div class="survey-row">
        <span class="survey-label" matTooltip="Did the extracted values look right?" matTooltipShowDelay="600">Accuracy</span>
        <span class="survey-stars">
          <button mat-icon-button *ngFor="let s of surveyStars"
            (click)="setAccuracyRating(s)"
            [class.survey-star-filled]="(surveyAccuracyRating ?? 0) >= s">
            <mat-icon>{{ (surveyAccuracyRating ?? 0) >= s ? 'star' : 'star_border' }}</mat-icon>
          </button>
        </span>
      </div>
      <div class="survey-row">
        <span class="survey-label" matTooltip="Is this config simple enough you'd be comfortable maintaining it?" matTooltipShowDelay="600">Simplicity</span>
        <span class="survey-stars">
          <button mat-icon-button *ngFor="let s of surveyStars"
            (click)="setSimplicityRating(s)"
            [class.survey-star-filled]="(surveySimplicityRating ?? 0) >= s">
            <mat-icon>{{ (surveySimplicityRating ?? 0) >= s ? 'star' : 'star_border' }}</mat-icon>
          </button>
        </span>
      </div>
      <div class="survey-row">
        <span class="survey-label" matTooltip="How fast did Copilot build this config?" matTooltipShowDelay="600">Copilot speed</span>
        <span class="survey-stars">
          <button mat-icon-button *ngFor="let s of surveyStars"
            (click)="setCopilotSpeedRating(s)"
            [class.survey-star-filled]="(surveyCopilotSpeedRating ?? 0) >= s">
            <mat-icon>{{ (surveyCopilotSpeedRating ?? 0) >= s ? 'star' : 'star_border' }}</mat-icon>
          </button>
        </span>
      </div>
      <div class="survey-row">
        <span class="survey-label" matTooltip="How fast does the DTR itself run (Run All)?" matTooltipShowDelay="600">DTR speed</span>
        <span class="survey-stars">
          <button mat-icon-button *ngFor="let s of surveyStars"
            (click)="setDtrSpeedRating(s)"
            [class.survey-star-filled]="(surveyDtrSpeedRating ?? 0) >= s">
            <mat-icon>{{ (surveyDtrSpeedRating ?? 0) >= s ? 'star' : 'star_border' }}</mat-icon>
          </button>
        </span>
      </div>
      <div class="survey-row">
        <span class="survey-label" matTooltip="How much back-and-forth did it take to get here?" matTooltipShowDelay="600">Friction</span>
        <span class="survey-stars">
          <button mat-icon-button *ngFor="let s of surveyStars"
            (click)="setFrictionRating(s)"
            [class.survey-star-filled]="(surveyFrictionRating ?? 0) >= s">
            <mat-icon>{{ (surveyFrictionRating ?? 0) >= s ? 'star' : 'star_border' }}</mat-icon>
          </button>
        </span>
      </div>
      <div class="survey-row">
        <span class="survey-label" matTooltip="Did you need to double-check Copilot's proposals, or take them as-is?" matTooltipShowDelay="600">Trust</span>
        <span class="survey-stars">
          <button mat-icon-button *ngFor="let s of surveyStars"
            (click)="setTrustRating(s)"
            [class.survey-star-filled]="(surveyTrustRating ?? 0) >= s">
            <mat-icon>{{ (surveyTrustRating ?? 0) >= s ? 'star' : 'star_border' }}</mat-icon>
          </button>
        </span>
      </div>
      <div class="survey-row survey-feedback-row">
        <div class="survey-feedback-field">
          <label class="survey-feedback-label">Additional comments (optional)</label>
          <textarea class="survey-feedback-textarea" maxlength="2000"
            [(ngModel)]="surveyFeedbackText"
            placeholder="Anything Copilot should have done differently?"></textarea>
        </div>
      </div>
      <div class="survey-actions">
        <button mat-button (click)="dismissSurvey()">Skip</button>
        <button mat-flat-button color="primary" (click)="submitSurvey()">Submit</button>
      </div>
    </div>

    <!-- DTR Copilot Config Analyser — Analysis Survey. Deliberately separate from the
         build survey above (Architect's decision), not a shared/generalized component. -->
    <div *ngIf="showAnalysisSurvey" class="copilot-survey">
      <div class="survey-title">
        <mat-icon class="survey-icon">rate_review</mat-icon>
        Analysis complete — how did I do?
      </div>
      <div class="survey-row">
        <span class="survey-label" matTooltip="Was the analysis genuinely useful, not just a restatement of the rules?" matTooltipShowDelay="600">Insight quality</span>
        <span class="survey-stars">
          <button mat-icon-button *ngFor="let s of surveyStars"
            (click)="setAnalysisInsightRating(s)"
            [class.survey-star-filled]="(analysisInsightRating ?? 0) >= s">
            <mat-icon>{{ (analysisInsightRating ?? 0) >= s ? 'star' : 'star_border' }}</mat-icon>
          </button>
        </span>
      </div>
      <div class="survey-row">
        <span class="survey-label" matTooltip="Did the coverage-gap / contamination commentary match what you already knew about the data?" matTooltipShowDelay="600">Accuracy of findings</span>
        <span class="survey-stars">
          <button mat-icon-button *ngFor="let s of surveyStars"
            (click)="setAnalysisAccuracyRating(s)"
            [class.survey-star-filled]="(analysisAccuracyRating ?? 0) >= s">
            <mat-icon>{{ (analysisAccuracyRating ?? 0) >= s ? 'star' : 'star_border' }}</mat-icon>
          </button>
        </span>
      </div>
      <div class="survey-row">
        <span class="survey-label" matTooltip="Were the suggestions something you could act on?" matTooltipShowDelay="600">Actionability</span>
        <span class="survey-stars">
          <button mat-icon-button *ngFor="let s of surveyStars"
            (click)="setAnalysisActionabilityRating(s)"
            [class.survey-star-filled]="(analysisActionabilityRating ?? 0) >= s">
            <mat-icon>{{ (analysisActionabilityRating ?? 0) >= s ? 'star' : 'star_border' }}</mat-icon>
          </button>
        </span>
      </div>
      <div class="survey-row">
        <span class="survey-label" matTooltip="Did Copilot correctly grasp what you were trying to achieve with this transformer?" matTooltipShowDelay="600">Goals understanding</span>
        <span class="survey-stars">
          <button mat-icon-button *ngFor="let s of surveyStars"
            (click)="setAnalysisGoalsUnderstandingRating(s)"
            [class.survey-star-filled]="(analysisGoalsUnderstandingRating ?? 0) >= s">
            <mat-icon>{{ (analysisGoalsUnderstandingRating ?? 0) >= s ? 'star' : 'star_border' }}</mat-icon>
          </button>
        </span>
      </div>
      <div class="survey-row">
        <span class="survey-label" matTooltip="Did you need to double-check Copilot's analysis, or take it as-is?" matTooltipShowDelay="600">Trust</span>
        <span class="survey-stars">
          <button mat-icon-button *ngFor="let s of surveyStars"
            (click)="setAnalysisTrustRating(s)"
            [class.survey-star-filled]="(analysisTrustRating ?? 0) >= s">
            <mat-icon>{{ (analysisTrustRating ?? 0) >= s ? 'star' : 'star_border' }}</mat-icon>
          </button>
        </span>
      </div>
      <div class="survey-row survey-feedback-row">
        <div class="survey-feedback-field">
          <label class="survey-feedback-label">Additional comments (optional)</label>
          <textarea class="survey-feedback-textarea" maxlength="2000"
            [(ngModel)]="analysisFeedbackText"
            placeholder="Anything Copilot should have done differently?"></textarea>
        </div>
      </div>
      <div class="survey-actions">
        <button mat-button (click)="dismissAnalysisSurvey()">Skip</button>
        <button mat-flat-button color="primary" (click)="submitAnalysisSurvey()">Submit</button>
      </div>
    </div>

    <!-- Input area -->
    <mat-card-actions class="input-area">
      <mat-form-field appearance="outline" class="chat-input-field">
        <textarea
          matInput
          [(ngModel)]="userInput"
          placeholder="Describe what you want... (Ctrl+Enter to send)"
          rows="7"
          [disabled]="isProcessing || !sessionId"
          (keydown.control.enter)="send()">
        </textarea>
      </mat-form-field>
      <button
        mat-fab
        [color]="(isProcessing || isStopping) ? 'warn' : 'primary'"
        class="send-btn"
        (click)="(isProcessing || isStopping) ? stopProcessing() : send()"
        [disabled]="!(isProcessing || isStopping) && (!userInput.trim() || !sessionId)"
        [matTooltip]="isStopping ? 'Force stop' : (isProcessing ? 'Stop (Esc)' : 'Send (Ctrl+Enter)')">
        <mat-icon>{{ (isProcessing || isStopping) ? 'stop' : 'send' }}</mat-icon>
      </button>
    </mat-card-actions>

    <div class="resize-handle" (mousedown)="onResizeStart($event)"></div>

  </mat-card>
</div>
